import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../services/supabase';

export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState(null);

  const loadUserRole = useCallback(async (userId) => {
    // console.log('loadUserRole called with userId:', userId);
    if (!userId) {
      setRole(null);
      return;
    }

    try {
      // Get user role directly with a single query
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .single();

      if (error) {
        console.error('Error fetching user role:', error);
        setRole('user');
        return;
      }

      // If no role found, default to 'user'
      if (!data) {
        setRole('user');
        return;
      }

      setRole(data.role);
    } catch (error) {
      console.error('Error loading user role:', error);
      setRole('user');
    }
  }, []);  // Empty dependency array since we don't use any external values

  const roleLoadedRef = useRef(false);

  // Debug re-renders
  // useEffect(() => {
  //   console.log('useAuth state changed:', {
  //     user: user?.id,
  //     role,
  //     loading,
  //     roleLoaded: roleLoadedRef.current
  //   });
  // }, [user, role, loading]);

  useEffect(() => {
    const initAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) throw error;
        
        const currentUser = session?.user ?? null;
        setUser(currentUser);
        
        // Only load role once when we first get a valid user
        if (currentUser && !roleLoadedRef.current) {
          await loadUserRole(currentUser.id);
          roleLoadedRef.current = true;
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
      } finally {
        setLoading(false);
      }
    };

    initAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        const newUser = session?.user ?? null;
        setUser(newUser);
        
        // Reset role and ref when user logs out
        if (!newUser) {
          setRole(null);
          roleLoadedRef.current = false;
        }
        
        setLoading(false);
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [loadUserRole]); // Add loadUserRole to dependencies

  const isAdmin = role === 'admin' || role === 'superadmin';
  const isSuperAdmin = role === 'superadmin';

  return { 
    user, 
    loading, 
    role,
    isAdmin,
    isSuperAdmin
  };
};
